"use client";

import type React from "react";

import { useState } from "react";
import {
  Box,
  Button,
  Input,
  Text,
  Flex,
  VStack,
  HStack,
  Container,
} from "@chakra-ui/react";
import { toaster } from "@/_components/lib/ui/toaster";
import { KnootLogo } from "@/_images/svgs/chakra-icons/KnootLogo";
import { Emoji } from "@/_components/ui/Emoji";
import useSWRMutation from "swr/mutation";
import { landingService } from "@/_services/landing";
import { emailRegex } from "@/_constants/regex";

export function SubcribeHero() {
  const [email, setEmail] = useState("");

  const { isMutating, trigger: subscriberTrigger } = useSWRMutation(
    "landing/subscribe",
    (_1, { arg }: { arg: string }) => landingService.sendSubscriberEmail(arg),
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (!email) {
        throw new Error("Email is required");
      }
      if (emailRegex.test(email) === false) {
        throw new Error("Invalid email address");
      }

      await subscriberTrigger(email);

      toaster.success({
        title: "Success!",
        description:
          "Thank you for subscribing! We will keep you updated with the latest news and updates.",
        duration: 4000,
      });

      setEmail("");
    } catch (error) {
      console.error(error);
      toaster.error({
        title: "Error",
        description: (error as Error).message,
        duration: 3000,
      });
    }
  };

  const SUBSCRIPTION_CONTENT = [
    {
      emoji: "robot" as const,
      question: "Tired of manually filtering resumes?",
      answer: "Smart resume ranking powered by AI",
    },
    {
      emoji: "money-with-wings" as const,
      question: "Wasted hours sorting & filtering resumes?",
      answer: "Save hours with our intelligent filtering engine",
    },
    {
      emoji: "globe-showing-americas" as const,
      question: "Your talent pool is everywhere?",
      answer: "Manage and track your talent pool in one place",
    },
    {
      emoji: "dizzy" as const,
      question: "Hard to find the best candidates?",
      answer: "Search and filter candidates across multiple sources",
    },
    {
      emoji: "collision" as const,
      question: "Special launch perks!",
      answer: (
        <>
          Enjoy{" "}
          <Text as="span" fontFamily="heading" color="red.400">
            3
          </Text>{" "}
          months free +{" "}
          <Text as="span" fontFamily="heading" color="blue.400">
            24/7
          </Text>{" "}
          support, and more!
        </>
      ),
    },
  ];

  const cdnUrl = process.env.NEXT_PUBLIC_CLOUDFRONT_CDN_URL;

  return (
    <Box
      position="relative"
      maxH={{ base: "auto", md: "100vh" }}
      overflow={{ base: "auto", md: "hidden" }}
      bgColor="white"
      backgroundImage={`url('${cdnUrl}/images/dot-grid.webp')`}
      backgroundSize="200px 200px"
      backgroundPosition="center"
      backgroundRepeat="repeat"
      py={8}
    >
      {/* Background Overlay */}
      <Box
        position="absolute"
        inset={0}
        bgGradient="linear(to-br, rgba(255,255,255,0.8), rgba(255,255,255,0.9), rgba(59,130,246,0.1))"
        zIndex={1}
      />

      {/* Content */}
      <Box position="relative" zIndex={2}>
        {/* Header */}
        <Flex align="center" justify="center">
          <HStack gap={2}>
            <Box
              boxSize={{ base: 8, md: 10 }}
              display="flex"
              alignItems="center"
              justifyContent="center"
              borderRadius="lg"
              bg="primary.500"
            >
              <KnootLogo />
            </Box>
            <Text
              fontSize="xl"
              fontFamily="heading"
              fontWeight="semibold"
              color="neutral.700"
            >
              Knoot.AI
            </Text>
            <Box
              bg="primary.50"
              px={2}
              py={1}
              borderRadius="full"
              fontSize="sm"
              display={{ base: "block", md: "none" }}
              ml={2}
            >
              <Flex gap={1} alignItems="center" justifyContent="center">
                <Emoji emoji="sparkles" size={3} />
                <Text fontWeight="semibold" textStyle="xs">
                  AI-Powered
                </Text>
              </Flex>
            </Box>
          </HStack>
        </Flex>

        {/* Hero Section */}
        <Container maxW="3xl" py={{ base: 20, md: "26" }}>
          <VStack gap={{ base: 10, md: 16 }} textAlign="center">
            {/* Main Headline */}
            <Flex
              flexDirection="column"
              alignItems={{ base: "flex-start", md: "center" }}
              justifyContent={{ base: "flex-start", md: "center" }}
              w="full"
              px={{ base: 2, md: 0 }}
              gap={2}
            >
              <Box
                bg="primary.50"
                px={4}
                py={1.5}
                borderRadius="full"
                fontSize="sm"
                display={{ base: "none", md: "block" }}
                shadow="md"
                shadowColor="neutral.200"
                mb={4}
              >
                <Flex gap={2} alignItems="center" justifyContent="center">
                  <Emoji emoji="sparkles" size={4} />
                  <Text fontWeight="semibold" textStyle="sm">
                    AI-Powered
                  </Text>
                </Flex>
              </Box>
              <Text
                textStyle={{ base: "xl", sm: "xl", lg: "2xl" }}
                fontWeight="semibold"
                color="neutral.700"
                fontFamily="body"
                fontStyle={{ base: "normal", md: "italic" }}
              >
                Get{" "}
                <Text
                  as="span"
                  textStyle="3xl"
                  fontFamily="body"
                  color="neutral.700"
                  fontWeight="bold"
                  position="relative"
                  _before={{
                    bgImage: `url('${cdnUrl}/images/brush-1.png')`,
                    content: '""',
                    position: "absolute",
                    bottom: -1,
                    left: -3,
                    width: "110%",
                    height: "110%",
                    backgroundSize: "contain",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                    zIndex: -1,
                  }}
                >
                  Early Access
                </Text>{" "}
                to the
              </Text>
              <Text
                fontFamily="heading"
                fontWeight="semibold"
                color="neutral.700"
                textStyle="5xl"
                textAlign={{ base: "left", md: "center" }}
              >
                AI Resume Analyzer
              </Text>
              <Text
                textStyle="md"
                color="primary.600"
                textAlign={{ base: "left", md: "center" }}
                fontWeight="semibold"
              >
                Let AI help you filter, rank, and manage resumes —{" "}
                <Text
                  as="span"
                  fontWeight="bold"
                  color="primary.600"
                  fontStyle="italic"
                >
                  EFFORTLESSLY!
                </Text>
              </Text>
            </Flex>

            {/* Subtext */}
            <Flex
              flexDirection="column"
              gap={4}
              alignItems="flex-start"
              justifyContent="flex-start"
              w={{ base: "full", md: "66%" }}
              px={{ base: 2, md: 0 }}
            >
              {SUBSCRIPTION_CONTENT.map((item) => (
                <Flex
                  key={item.emoji}
                  gap={3}
                  alignItems="center"
                  justifyContent="flex-start"
                >
                  <Emoji emoji={item.emoji} size={6} />
                  <Text textAlign="left">
                    <Text
                      as="span"
                      fontStyle="italic"
                      color="neutral.700"
                      fontWeight="bold"
                      position="relative"
                      _before={{
                        bgImage: `url('${cdnUrl}/images/brush-3.png')`,
                        content: '""',
                        position: "absolute",
                        bottom: -1,
                        left: -2,
                        width: "110%",
                        height: "100%",
                        opacity: 0.5,
                        backgroundSize: "contain",
                        backgroundPosition: "center",
                        backgroundRepeat: "no-repeat",
                        zIndex: -1,
                      }}
                    >
                      {item.question}
                    </Text>
                    <br />
                    <Text as="span" color="neutral.700">
                      {item.answer}
                    </Text>
                  </Text>
                </Flex>
              ))}
            </Flex>

            {/* Email Form */}
            <Box
              as="form"
              onSubmit={handleSubmit}
              maxW="md"
              w="full"
              position="relative"
            >
              <Box
                bgImage={`url('${cdnUrl}/images/brush-4.png')`}
                position="absolute"
                bottom="20px"
                left="-110px"
                rotate="30deg"
                width="100px"
                height="100px"
                backgroundSize="contain"
                backgroundPosition="center"
                backgroundRepeat="no-repeat"
                zIndex="-1"
                visibility={{ base: "hidden", md: "visible" }}
              />
              <VStack gap={3}>
                <Flex
                  gap={3}
                  w="full"
                  pt={{ base: 4, md: 0 }}
                  px={{ base: 4, md: 0 }}
                  flexDirection={{ base: "column", md: "row" }}
                >
                  <Input
                    colorPalette="primary"
                    rounded="full"
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    h={12}
                    flex={1}
                    disabled={isMutating}
                    bg="white"
                    border="1px solid"
                    borderColor="neutral.400"
                    _focus={{
                      borderColor: "primary.500",
                      ringColor: "primary.500",
                    }}
                    fontFamily="heading"
                    p={4}
                  />
                  <Button
                    rounded="full"
                    type="submit"
                    size="lg"
                    w={{ base: "full", md: "auto" }}
                    h={12}
                    px={8}
                    disabled={isMutating}
                    loading={isMutating}
                    loadingText="Subscribing..."
                    colorPalette="primary"
                    fontWeight="semibold"
                    shadow="sm"
                    _hover={{ shadow: "xl", boxShadow: "lg" }}
                    transition="all 0.2s ease-in-out"
                  >
                    Subscribe
                  </Button>
                </Flex>

                {/* Privacy text */}
                <Text fontSize="xs" color="neutral.700" fontStyle="italic">
                  No spam. You will receive an email once we launch. We respect
                  your privacy.
                </Text>
              </VStack>
            </Box>
          </VStack>
        </Container>
      </Box>
    </Box>
  );
}
